import { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { useGetEmployeeAvailabilityForRangeQuery, useGetCalendarViewQuery } from '../../../api/serverStateHooks'
import AppDateInput from '../../../components/AppDateInput'
import AppTimeInput from '../../../components/AppTimeInput'
import { getUserDetail } from '../../masters/users/api/userApi'

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const HOUR_LABELS = Array.from({ length: 24 }).map((_, hour) => {
  if (hour === 0) return '12 AM'
  if (hour < 12) return `${hour} AM`
  if (hour === 12) return '12 PM'
  return `${hour - 12} PM`
})
const DAY_ROW_HEIGHT = 56

// ─────────────────────────────────────────────────────────────────────────────
// Date helpers
// ─────────────────────────────────────────────────────────────────────────────
function toDateKey(d) {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function toHHMM(hour, minute = 0) {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

function toTimeLabel(hour, minute = 0) {
  const period = hour >= 12 ? 'PM' : 'AM'
  const normalized = hour % 12 || 12
  if (minute === 0) return `${normalized} ${period}`
  return `${normalized}:${String(minute).padStart(2, '0')} ${period}`
}

// Parse a naive datetime ISO string (no timezone info) as local time.
// Without this, `new Date('2026-07-01T12:00:00+00:00')` would shift the
// displayed time by the browser's UTC offset (e.g. +5:30 → 5:30 PM instead of 12:00 PM).
function parseNaiveDate(iso) {
  if (!iso) return new Date(NaN)
  // Strip any timezone offset / 'Z' suffix so the browser treats it as local time
  const match = iso.match(/^(\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2})/)
  if (match) {
    return new Date(match[1].replace(' ', 'T'))
  }
  return new Date(iso)
}

function formatClock(iso) {
  if (!iso) return '--'
  const date = parseNaiveDate(iso)
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

function resolveEventPalette(event) {
  if (event.scheduleType === 'LEAVE' || event.scheduleType === 'HOLIDAY') {
    return {
      chip: 'bg-red-600 text-white',
      block: 'border-red-700 bg-red-100 text-red-900',
    }
  }
  if (event.eventType === 'meeting') {
    return {
      chip: 'bg-emerald-700 text-white',
      block: 'border-emerald-700 bg-emerald-100 text-emerald-900',
    }
  }
  if (event.scheduleType === 'AVAILABILITY') {
    return {
      chip: 'bg-blue-600 text-white',
      block: 'border-blue-600 bg-blue-100 text-blue-900',
    }
  }
  return {
    chip: 'bg-amber-500 text-white',
    block: 'border-amber-500 bg-amber-100 text-amber-900',
  }
}

function getHorizontalStyle(startTimeStr, endTimeStr, timelineStartHour, cellWidth) {
  const start = new Date(startTimeStr)
  const end = new Date(endTimeStr)
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null

  // Calculate minutes since the start of the timeline
  const timelineStartMin = timelineStartHour * 60
  
  const startMin = start.getHours() * 60 + start.getMinutes()
  const endMinRaw = end.getHours() * 60 + end.getMinutes()
  const endMin = endMinRaw <= startMin ? startMin + 30 : endMinRaw
  
  const leftPx = Math.max(0, ((startMin - timelineStartMin) / 60) * cellWidth)
  const widthPx = Math.max(20, ((endMin - startMin) / 60) * cellWidth)
  
  return {
    left: `${leftPx}px`,
    width: `${widthPx}px`
  }
}

function parseTimeHour(timeStr, defaultHour) {
  if (!timeStr) return defaultHour
  const match = timeStr.match(/^(\d{1,2}):/)
  if (match) {
    return parseInt(match[1], 10)
  }
  return defaultHour
}

function isHourFree(hour, dateStr, events) {
  const hourStart = new Date(`${dateStr}T${String(hour).padStart(2, '0')}:00:00`)
  const hourEnd = new Date(`${dateStr}T${String(hour + 1).padStart(2, '0')}:00:00`)
  
  for (const event of events) {
    const eventStart = parseNaiveDate(event.startTime)
    const eventEnd = parseNaiveDate(event.endTime)
    if (eventStart < hourEnd && eventEnd > hourStart) {
      return false
    }
  }
  return true
}

function getFreeIntervals(dayEvents, dateStr, shiftStartMin, shiftEndMin) {
  let freeIntervals = [{ start: shiftStartMin, end: shiftEndMin }]
  
  const busyIntervals = dayEvents.map(event => {
    const start = parseNaiveDate(event.startTime)
    const end = parseNaiveDate(event.endTime)
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null

    // For Job events, only treat as busy (subtracted) if duration and planned hours match exactly
    if (event.scheduleType === 'JOB') {
      const durationHrs = (end.getTime() - start.getTime()) / 3600000
      const plannedHrs = event.plannedHours != null ? parseFloat(event.plannedHours) : 0
      if (Math.abs(durationHrs - plannedHrs) > 0.01) {
        return null
      }
    }

    const startMin = start.getHours() * 60 + start.getMinutes()
    const endMinRaw = end.getHours() * 60 + end.getMinutes()
    const endMin = endMinRaw <= startMin ? startMin + 30 : endMinRaw
    return { start: startMin, end: endMin }
  }).filter(Boolean).filter(bi => bi.start < shiftEndMin && bi.end > shiftStartMin)
  
  for (const busy of busyIntervals) {
    const nextFree = []
    for (const free of freeIntervals) {
      if (busy.start >= free.end || busy.end <= free.start) {
        nextFree.push(free)
      } else {
        if (busy.start > free.start) {
          nextFree.push({ start: free.start, end: busy.start })
        }
        if (busy.end < free.end) {
          nextFree.push({ start: busy.end, end: free.end })
        }
      }
    }
    freeIntervals = nextFree
  }
  
  return freeIntervals
}

function assignLanesLocal(rawBlocks) {
  // Sort by start date ascending (nulls last)
  const sorted = [...rawBlocks].sort((a, b) => {
    if (!a.start && !b.start) return 0
    if (!a.start) return 1
    if (!b.start) return -1
    return a.start - b.start
  })

  const laneEnds = [] // laneEnds[i] = latest end Date in lane i

  const withLane = sorted.map((block) => {
    const s = block.start
    const e = block.end

    // Find first lane where this block fits (doesn't overlap)
    let placed = -1
    for (let l = 0; l < laneEnds.length; l++) {
      if (!s || !laneEnds[l] || s >= laneEnds[l]) {
        placed = l
        break
      }
    }
    if (placed === -1) {
      placed = laneEnds.length
      laneEnds.push(null)
    }
    if (e) {
      laneEnds[placed] = !laneEnds[placed] || e > laneEnds[placed] ? e : laneEnds[placed]
    }

    return { ...block, lane: placed }
  })

  return { lanes: Math.max(1, laneEnds.length), blocks: withLane }
}


function getEventBlockStyle(event, dayHeight) {
  const start = new Date(event.startTime)
  const end = new Date(event.endTime)
  const startMin = start.getHours() * 60 + start.getMinutes()
  const endMinRaw = end.getHours() * 60 + end.getMinutes()
  const endMin = endMinRaw <= startMin ? startMin + 30 : endMinRaw
  const top = (startMin / 1440) * dayHeight
  const height = Math.max(((endMin - startMin) / 1440) * dayHeight, 24)
  return { top: `${top}px`, height: `${height}px` }
}

function getSlotRangeFromClick(event, dayHeight) {
  const rect = event.currentTarget.getBoundingClientRect()
  const y = Math.max(0, Math.min(event.clientY - rect.top, dayHeight - 1))
  const slotHour = Math.floor(y / DAY_ROW_HEIGHT)
  const startHour = Math.max(0, Math.min(slotHour, 23))
  const endHour = Math.min(startHour + 1, 23)
  const endTime = startHour === 23 ? '23:59' : toHHMM(endHour)
  return {
    startTime: toHHMM(startHour),
    endTime,
    slotLabel: `${toTimeLabel(startHour)} - ${startHour === 23 ? '11:59 PM' : toTimeLabel(endHour)}`,
  }
}

function parseDate(str) {
  if (!str) return null
  const d = new Date(str)
  return isNaN(d.getTime()) ? null : d
}

function formatMonthLabel(year, month) {
  return new Date(year, month - 1, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  })
}

function buildCalendarMatrix(year, month) {
  const firstDay = new Date(year, month - 1, 1)
  const daysInMonth = new Date(year, month, 0).getDate()
  const startOffset = firstDay.getDay()
  const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7
  const cells = []
  for (let i = 0; i < totalCells; i++) {
    const dayNum = i - startOffset + 1
    if (dayNum < 1 || dayNum > daysInMonth) {
      cells.push(null)
    } else {
      const d = new Date(year, month - 1, dayNum)
      cells.push({ dayNum, date: d, key: toDateKey(d) })
    }
  }
  return cells
}

// ─────────────────────────────────────────────────────────────────────────────
// AllocationAssignmentModal Component
// ─────────────────────────────────────────────────────────────────────────────
export default function AllocationAssignmentModal({
  employee,
  preSelectedTask,
  suggestedDates,
  month,
  onAssign,
  onClose,
  isAssigning,
  assignWarnings,
  isUpdate,
  onRefresh,
  isRefreshing,
  mode = 'assign', // 'assign' | 'select'
}) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    // If suggestedDates has a start, open to that month
    if (suggestedDates?.start) {
      const d = new Date(suggestedDates.start)
      if (!isNaN(d.getTime())) {
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      }
    }
    // If preSelectedTask has a planned_start, open to that month
    if (preSelectedTask?.planned_start) {
      const d = new Date(preSelectedTask.planned_start)
      if (!isNaN(d.getTime())) {
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      }
    }
    return month
  })
  const [year, mon] = currentMonth.split('-').map(Number)
  const calendarCells = useMemo(() => buildCalendarMatrix(year, mon), [year, mon])

  // Pre-populate dates from existing task block if editing
  const initStart = useMemo(() => {
    if (suggestedDates?.start) {
      const d = new Date(suggestedDates.start)
      return isNaN(d.getTime()) ? null : d
    }
    if (preSelectedTask?.planned_start) {
      const d = new Date(preSelectedTask.planned_start)
      return isNaN(d.getTime()) ? null : d
    }
    return null
  }, [preSelectedTask, suggestedDates])
  const initEnd = useMemo(() => {
    if (suggestedDates?.end) {
      const d = new Date(suggestedDates.end)
      return isNaN(d.getTime()) ? null : d
    }
    if (preSelectedTask?.planned_end) {
      const d = new Date(preSelectedTask.planned_end)
      return isNaN(d.getTime()) ? null : d
    }
    return null
  }, [preSelectedTask, suggestedDates])

  const [rangeStart, setRangeStart] = useState(initStart)
  const [rangeEnd, setRangeEnd] = useState(initEnd)
  const initialStartRef = useRef(initStart)
  const initialEndRef = useRef(initEnd)
  const [estimatedHours, setEstimatedHours] = useState(
    preSelectedTask?.effort_estimate != null ? String(preSelectedTask.effort_estimate) : ''
  )
  const [requiresConfirm, setRequiresConfirm] = useState(false)

  const timelineRef = useRef(null)
  const timelineScrollContainerRef = useRef(null)
  const bottomFieldsRef = useRef(null)
  const [activeDate, setActiveDate] = useState(() => initStart || new Date())
  const [startTime, setStartTime] = useState(preSelectedTask?.planned_start_time || '')
  const [endTime, setEndTime] = useState(preSelectedTask?.planned_end_time || '')
  const [plannedHours, setPlannedHours] = useState(
    preSelectedTask?.planned_hours_value != null ? String(preSelectedTask.planned_hours_value) : '0'
  )
  const [plannedMinutes, setPlannedMinutes] = useState(
    preSelectedTask?.planned_minutes_value != null ? String(preSelectedTask.planned_minutes_value) : '0'
  )

  const activeDateKey = useMemo(() => toDateKey(activeDate), [activeDate])
  const { data: calendarData, isLoading: isCalendarLoading } = useGetCalendarViewQuery(
    {
      view: 'day',
      from: activeDateKey,
      user_id: employee?.employee_id,
    },
    { skip: mode !== 'select-job' || !employee?.employee_id }
  )

  const dayEvents = useMemo(() => {
    const items = calendarData?.items || []
    return items
      .filter((event) => {
        // Exclude the job currently being edited from the busy-events list so it
        // doesn't block its own time slot in the availability view.
        if (
          mode === 'select-job' &&
          preSelectedTask &&
          String(event.jobId) === String(preSelectedTask.task_id)
        ) {
          return false
        }
        return true
      })
      .slice()
      .sort((a, b) => parseNaiveDate(a.startTime) - parseNaiveDate(b.startTime))
  }, [calendarData, mode, preSelectedTask])

  const [employeeShift, setEmployeeShift] = useState({ intime: '10:00', outtime: '19:00' })

  useEffect(() => {
    if (!employee?.employee_id) return
    let active = true
    getUserDetail(employee.employee_id)
      .then((data) => {
        if (active && data) {
          setEmployeeShift({
            intime: data.shift_intime || '10:00',
            outtime: data.shift_outtime || '19:00'
          })
        }
      })
      .catch(() => {
        if (active) {
          setEmployeeShift({ intime: '10:00', outtime: '19:00' })
        }
      })
    return () => {
      active = false
    }
  }, [employee?.employee_id])

  useEffect(() => {
    if (mode !== 'select-job') return
    const timer = setTimeout(() => {
      if (!timelineScrollContainerRef.current) return
      
      const parsedStart = parseTimeHour(employeeShift?.intime, 10)
      const parsedEnd = parseTimeHour(employeeShift?.outtime, 19)
      const timelineStartHour = Math.max(0, parsedStart - 2)
      const timelineEndHour = Math.min(23, parsedEnd + 2)
      
      // Find first free hour
      let targetHour = parsedStart
      for (let h = timelineStartHour; h <= timelineEndHour; h++) {
        if (isHourFree(h, activeDateKey, dayEvents)) {
          targetHour = h
          break
        }
      }
      
      const idx = Math.max(0, targetHour - timelineStartHour)
      const cellWidth = 140
      timelineScrollContainerRef.current.scrollTo({
        left: idx * cellWidth,
        behavior: 'smooth'
      })
    }, 250)
    
    return () => clearTimeout(timer)
  }, [activeDateKey, dayEvents, employeeShift, mode])

  const handleFreeSlotClick = (free) => {
    const startHour = Math.floor(free.start / 60)
    const startMin = free.start % 60
    const endHour = Math.floor(free.end / 60)
    const endMin = free.end % 60
    
    const startStr = toHHMM(startHour, startMin)
    const endStr = toHHMM(endHour, endMin)
    
    setStartTime(startStr)
    setEndTime(endStr)
    
    const activeAvail = availMap[activeDateKey]
    const rawFreeVal = activeAvail ? activeAvail.free : baseHours

    const totalMin = free.end - free.start
    const slotHrs = totalMin / 60
    const fillHrs = Math.min(slotHrs, rawFreeVal)

    const fillHrsInteger = Math.floor(fillHrs)
    const fillMinsFraction = Math.round((fillHrs - fillHrsInteger) * 60)

    setPlannedHours(String(fillHrsInteger))
    setPlannedMinutes(String(fillMinsFraction))

    setTimeout(() => {
      bottomFieldsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 50)
  }


  const monthStart = `${year}-${String(mon).padStart(2, '0')}-01`
  const daysInMonth = new Date(year, mon, 0).getDate()
  const monthEnd = `${year}-${String(mon).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`

  const queryStart = useMemo(() => {
    let startStr = monthStart
    if (rangeStart) {
      const sKey = toDateKey(rangeStart)
      if (sKey < startStr) {
        startStr = sKey
      }
    }
    if (rangeEnd) {
      const eKey = toDateKey(rangeEnd)
      if (eKey < startStr) {
        startStr = eKey
      }
    }
    return startStr
  }, [monthStart, rangeStart, rangeEnd])

  const queryEnd = useMemo(() => {
    let endStr = monthEnd
    if (rangeStart) {
      const sKey = toDateKey(rangeStart)
      if (sKey > endStr) {
        endStr = sKey
      }
    }
    if (rangeEnd) {
      const eKey = toDateKey(rangeEnd)
      if (eKey > endStr) {
        endStr = eKey
      }
    }
    return endStr
  }, [monthEnd, rangeStart, rangeEnd])

  const { data: availData } = useGetEmployeeAvailabilityForRangeQuery({
    employee_id: employee.employee_id,
    start_date: queryStart,
    end_date: queryEnd,
    // When editing a task: exclude that task's own jobs from allocated hours
    exclude_task_id: isUpdate && preSelectedTask && mode !== 'select-job' ? preSelectedTask.task_id : undefined,
    // When editing a job (select-job mode): preSelectedTask.task_id is actually the job id
    exclude_job_id: isUpdate && preSelectedTask && mode === 'select-job' ? preSelectedTask.task_id : undefined,
  })

  const availMap = useMemo(() => {
    const map = {}
    const rows = Array.isArray(availData) ? availData : (employee.available_slot?.daily_rows || [])
    rows.forEach((r) => {
      const key = r.date || r.availability_date
      if (key) map[key] = {
        free: parseFloat(r.free_hours || 0),
        base: parseFloat(r.base_hours || employee.base_hours_per_day || 8),
        dayType: r.day_type || 'WORKING',
      }
    })
    return map
  }, [availData, employee])

  const baseHours = employee.base_hours_per_day || 8

  const selStart = rangeStart && rangeEnd
    ? (rangeStart <= rangeEnd ? rangeStart : rangeEnd)
    : rangeStart || rangeEnd
  const selEnd = rangeStart && rangeEnd
    ? (rangeStart <= rangeEnd ? rangeEnd : rangeStart)
    : rangeStart || rangeEnd

  // ── Simulated daily capacity consumption ────────────────────────────────────
  const simulatedAlloc = useMemo(() => {
    const result = {}
    if (!preSelectedTask || !selStart || !selEnd) return result

    const effort = mode === 'select-job'
      ? (parseFloat(plannedHours || 0) + parseFloat(plannedMinutes || 0) / 60)
      : parseFloat(estimatedHours || preSelectedTask?.effort_estimate || 0)

    if (effort <= 0) return result

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const eligible = []
    const cur = new Date(selStart)
    while (cur <= selEnd) {
      const key = toDateKey(cur)
      const av = availMap[key]
      const dt = av?.dayType || 'WORKING'
      const isWorking = dt !== 'WEEKEND' && dt !== 'HOLIDAY' && dt !== 'LEAVE_FULL'
      const isEligible = mode === 'select-job' || (isWorking && cur >= today)
      if (isEligible) eligible.push(key)
      cur.setDate(cur.getDate() + 1)
    }

    let remaining = effort
    eligible.forEach((key, idx) => {
      if (remaining <= 0) return
      const av = availMap[key]
      const free = av ? av.free : baseHours
      const daysLeft = eligible.length - idx
      const isLast = idx === eligible.length - 1
      const target = isLast ? remaining : Math.min(free > 0 ? free : baseHours, remaining / daysLeft)
      const take = Math.round(Math.min(free > 0 ? free : baseHours, target) * 10) / 10
      if (take > 0) {
        result[key] = take
        remaining = Math.round((remaining - take) * 10) / 10
      }
    })

    return result
  }, [preSelectedTask, selStart, selEnd, estimatedHours, plannedHours, plannedMinutes, availMap, baseHours, mode])

  const isSelectedRangeSet = !!(rangeStart && rangeEnd)

  function isInRange(date) {
    if (!selStart || !selEnd || !date) return false
    return date >= selStart && date <= selEnd
  }

  function handleCellClick(cell) {
    if (!cell) return
    const d = cell.date
    setActiveDate(d)
    
    if (mode === 'select-job') {
      setRangeStart(d)
      setRangeEnd(d)
    } else {
      if (!rangeStart) {
        setRangeStart(d)
        setRangeEnd(null)
      } else if (!rangeEnd) {
        setRangeEnd(d)
      } else {
        setRangeStart(d)
        setRangeEnd(null)
        setEstimatedHours('')
        setRequiresConfirm(false)
      }
    }

    if (mode === 'select-job') {
      setTimeout(() => {
        timelineRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 50)
    }
  }

  useEffect(() => {
    if (mode === 'select-job') return

    const sTime = rangeStart ? new Date(rangeStart).setHours(0, 0, 0, 0) : null
    const eTime = rangeEnd ? new Date(rangeEnd).setHours(0, 0, 0, 0) : null
    const initSTime = initialStartRef.current ? new Date(initialStartRef.current).setHours(0, 0, 0, 0) : null
    const initETime = initialEndRef.current ? new Date(initialEndRef.current).setHours(0, 0, 0, 0) : null

    const datesChanged = sTime !== initSTime || eTime !== initETime

    if (rangeStart && rangeEnd) {
      if (!datesChanged && preSelectedTask?.effort_estimate != null) {
        setEstimatedHours(String(preSelectedTask.effort_estimate))
        return
      }

      const s = rangeStart <= rangeEnd ? rangeStart : rangeEnd
      const e = rangeStart <= rangeEnd ? rangeEnd : rangeStart
      let total = 0
      const cur = new Date(s)
      while (cur <= e) {
        const day = cur.getDay()
        const isWeekend = day === 0 || day === 6
        if (!isWeekend) {
          const key = toDateKey(cur)
          const av = availMap[key]
          if (av) {
            const isNonWorking = av.dayType === 'HOLIDAY' || av.dayType === 'LEAVE_FULL'
            if (!isNonWorking) {
              total += av.free > 0 ? av.free : av.base
            }
          } else {
            total += baseHours
          }
        }
        cur.setDate(cur.getDate() + 1)
      }
      setEstimatedHours(String(Math.round(total * 10) / 10))
    } else {
      if (!datesChanged && preSelectedTask?.effort_estimate != null) {
        setEstimatedHours(String(preSelectedTask.effort_estimate))
      } else {
        setEstimatedHours('')
      }
    }
  }, [rangeStart, rangeEnd, availMap, baseHours, preSelectedTask, mode])


  function handlePrevMonth() {
    setCurrentMonth((prev) => {
      const [y, m] = prev.split('-').map(Number)
      const prevDate = new Date(y, m - 2, 1)
      return `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`
    })
  }

  function handleNextMonth() {
    setCurrentMonth((prev) => {
      const [y, m] = prev.split('-').map(Number)
      const nextDate = new Date(y, m, 1)
      return `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}`
    })
  }

  function handleAssign() {
    const s = selStart ? toDateKey(selStart) : null
    const e = selEnd ? toDateKey(selEnd) : null
    const hrs = parseFloat(estimatedHours) || null

    if (mode === 'select-job') {
      onAssign({
        planned_start: s || undefined,
        planned_end: e || undefined,
        planned_start_time: startTime || undefined,
        planned_end_time: endTime || undefined,
        planned_hours_value: plannedHours,
        planned_minutes_value: plannedMinutes,
      })
      return
    }

    if (mode === 'select') {
      onAssign({
        planned_start: s ? `${s}T00:00:00` : undefined,
        planned_end: e ? `${e}T00:00:00` : undefined,
        estimated_hours: hrs,
      })
      return
    }

    if (!preSelectedTask) return
    onAssign({
      task_id: Number(preSelectedTask.task_id),
      employee_id: employee.employee_id,
      planned_start: s ? `${s}T00:00:00` : undefined,
      planned_end: e ? `${e}T00:00:00` : undefined,
      estimated_hours: hrs,
      isUpdate,
      ignore_warnings: requiresConfirm,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div className="w-full max-w-3xl overflow-hidden rounded-[2rem] border border-outline-variant/20 bg-surface-container-lowest shadow-2xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between border-b border-outline-variant/10 bg-surface-container-lowest px-8 py-6 shrink-0">
          <div>
            <span className="text-[10px] font-bold text-outline uppercase tracking-widest block mb-1">
              {preSelectedTask
                ? (isUpdate
                    ? (mode === 'select-job' ? `Edit Job — ${employee.name}` : `Edit Task — ${employee.name}`)
                    : (mode === 'select-job' ? `Assign Job — ${employee.name}` : `Assign Task — ${employee.name}`))
                : `Check Availability — ${employee.name}`}
            </span>
            <h2 className="text-xl font-extrabold text-on-surface">
              Resource Assignment &amp; Availability
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onRefresh}
              disabled={isRefreshing}
              title="Refresh availability for this employee"
              className="rounded-full p-2 text-on-surface-variant hover:bg-surface-container transition-colors disabled:opacity-50"
            >
              <span className={clsx('material-symbols-outlined text-base', isRefreshing && 'animate-spin')}>
                refresh
              </span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-2 text-on-surface-variant hover:bg-surface-container transition-colors"
            >
              <span className="material-symbols-outlined text-base">close</span>
            </button>
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="p-8 pt-0 overflow-y-auto space-y-6 flex-1">
          {preSelectedTask ? (
            <div className="flex items-center gap-3 rounded-2xl border border-primary/20 bg-primary/5 p-4 shrink-0">
              <span className="material-symbols-outlined text-primary text-xl shrink-0">
                {isUpdate ? 'edit_calendar' : 'assignment_ind'}
              </span>
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-wider text-primary">
                  {isUpdate
                    ? (mode === 'select-job' ? 'Editing Job' : 'Editing Allocation')
                    : (mode === 'select-job' ? 'Assigning Job' : 'Assigning Task')}
                </p>
                <p className="text-sm font-bold text-on-surface truncate text-outline">
                  [{preSelectedTask.task_code}] {preSelectedTask.task_name} — {preSelectedTask.project_name}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 rounded-2xl border border-outline-variant/30 bg-surface-container-low p-4 shrink-0">
              <span className="material-symbols-outlined text-outline text-xl shrink-0">calendar_month</span>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-outline">
                  Availability Check Mode
                </p>
                <p className="text-xs text-on-surface-variant">
                  Check free hours and capacity for {employee.name}.
                </p>
              </div>
            </div>
          )}

          {/* Calendar grid */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="font-label text-[10px] font-bold uppercase tracking-wider">
                Select Date Range — Click start then end date
              </p>
              <div className="flex items-center gap-2 bg-surface-container-low px-3 py-1 rounded-full border border-outline-variant/20">
                <button
                  type="button"
                  onClick={handlePrevMonth}
                  className="p-1 hover:bg-surface-container rounded-full text-on-surface-variant hover:text-primary transition-colors flex items-center justify-center border-0 bg-transparent cursor-pointer"
                >
                  <span className="material-symbols-outlined text-base select-none">chevron_left</span>
                </button>
                <span className="text-xs font-bold text-on-surface min-w-[80px] text-center select-none">
                  {formatMonthLabel(year, mon)}
                </span>
                <button
                  type="button"
                  onClick={handleNextMonth}
                  className="p-1 hover:bg-surface-container rounded-full text-on-surface-variant hover:text-primary transition-colors flex items-center justify-center border-0 bg-transparent cursor-pointer"
                >
                  <span className="material-symbols-outlined text-base select-none">chevron_right</span>
                </button>
              </div>
            </div>
            <div className="overflow-hidden rounded-[16px] border border-outline-variant/20 bg-surface-container-lowest">
              {/* Weekday headers */}
              <div className="grid grid-cols-7 border-b border-surface-container">
                {WEEKDAYS.map((d) => (
                  <div key={d} className="px-2 py-3 text-center text-[10px] font-bold uppercase tracking-[0.14em] text-on-surface-variant bg-surface-container-low/30">
                    {d}
                  </div>
                ))}
              </div>

              {/* Day cells */}
              <div className="grid grid-cols-7">
                {calendarCells.map((cell, idx) => {
                  if (!cell) {
                    return (
                      <div
                        key={`empty-${idx}`}
                        className="min-h-[84px] border-b border-r border-surface-container bg-surface-container-low/55 px-2 py-2"
                      />
                    )
                  }
                  const key = cell.key
                  const av = availMap[key]
                  const dayType = av?.dayType || 'WORKING'
                  const inSel = isInRange(cell.date)
                  const isS = selStart && toDateKey(selStart) === key
                  const isE = selEnd && toDateKey(selEnd) === key

                  const simTaken = simulatedAlloc[key] || 0
                  const rawFree = av ? av.free : baseHours
                  const simFree = Math.max(0, Math.round((rawFree - simTaken) * 10) / 10)
                  const effectiveFree = simTaken > 0 ? simFree : rawFree

                  const isNonWorking = dayType === 'WEEKEND' || dayType === 'HOLIDAY' || dayType === 'LEAVE_FULL'
                  const isHalfDay = dayType === 'LEAVE_HALF'
                  const hasPartialFree = !isNonWorking && !isHalfDay && av && effectiveFree > 0 && effectiveFree < (av?.base || baseHours)
                  const isFullyOccupied = !isNonWorking && !isHalfDay && av && effectiveFree <= 0 && (av?.base || baseHours) > 0
                  const isWorkingFull = !isNonWorking && !isHalfDay && !hasPartialFree && !isFullyOccupied

                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleCellClick(cell)}
                      className={clsx(
                        'min-h-[84px] border-b border-r border-surface-container p-2 text-left align-top transition-all flex flex-col justify-between',
                        isNonWorking && 'bg-surface-container-low/60',
                        inSel && !isNonWorking && 'bg-blue-50/50',
                        (isS || isE) && 'ring-2 ring-inset ring-primary bg-blue-50',
                      )}
                    >
                      <div className="flex items-center justify-between w-full">
                        <span className={clsx(
                          'inline-flex h-6 min-w-6 items-center justify-center rounded-full text-xs font-bold',
                          isS || isE ? 'bg-primary text-white' : isNonWorking ? 'text-outline/60' : 'text-on-surface',
                        )}>
                          {cell.dayNum}
                        </span>
                        {simTaken > 0 && !isNonWorking && (
                          <span className="text-[9px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded-full leading-none">
                            -{simTaken}h
                          </span>
                        )}
                      </div>

                      {isNonWorking && (
                        <div className="w-full truncate rounded-full px-2 py-1 text-center text-[10px] font-bold bg-surface-container text-outline">
                          {dayType === 'WEEKEND' ? 'Weekend'
                            : dayType === 'HOLIDAY' ? 'Holiday'
                              : 'Leave (Full)'}
                        </div>
                      )}

                      {isHalfDay && (
                        <div className="w-full truncate rounded-full px-2 py-1 text-center text-[10px] font-bold bg-amber-100 text-amber-700">
                          Leave (Half){effectiveFree > 0 ? ` · ${effectiveFree}h` : ''}
                        </div>
                      )}

                      {hasPartialFree && (
                        <div className="w-full truncate rounded-xl px-2 py-1 text-center text-[10px] font-bold border-2 border-dashed border-primary bg-white text-primary">
                          {effectiveFree}h free
                        </div>
                      )}

                      {isWorkingFull && (
                        <div className={clsx(
                          'w-full truncate rounded-full px-2 py-1 text-center text-[10px] font-bold',
                          isS || isE ? 'bg-primary/10 text-primary' : 'bg-blue-100 text-blue-700'
                        )}>
                          {(av?.base || baseHours)}h capacity
                        </div>
                      )}

                      {isFullyOccupied && (
                        <div className="w-full truncate rounded-full px-2 py-1 text-center text-[10px] font-bold bg-error/10 text-error">
                          {'Fully' + '\n' + 'Occupied'}
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Day Timeline Section */}
          {mode === 'select-job' && (() => {
            const parsedStart = parseTimeHour(employeeShift?.intime, 10)
            const parsedEnd = parseTimeHour(employeeShift?.outtime, 19)
            const timelineStartHour = Math.max(0, parsedStart - 2)
            const timelineEndHour = Math.min(23, parsedEnd + 2)
            const totalHours = timelineEndHour - timelineStartHour + 1
            const cellWidth = 140

            const [startH, startM] = (employeeShift?.intime || '10:00').split(':').map(Number)
            const [endH, endM] = (employeeShift?.outtime || '19:00').split(':').map(Number)
            const shiftStartMin = startH * 60 + (startM || 0)
            const shiftEndMin = endH * 60 + (endM || 0)

            const activeAvail = availMap[activeDateKey]
            const rawFree = activeAvail ? activeAvail.free : baseHours

            const freeIntervals = getFreeIntervals(dayEvents, activeDateKey, shiftStartMin, shiftEndMin)

            // Build list of blocks for local lane assignment
            const rawBlocks = []

            // 1. Busy events
            dayEvents.forEach((event) => {
              const start = parseNaiveDate(event.startTime)
              const end = parseNaiveDate(event.endTime)
              if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
                rawBlocks.push({
                  id: `event-${event.id || event.startTime}`,
                  start,
                  end,
                  type: 'event',
                  data: event,
                })
              }
            })

            // 2. Selection highlight
            if (startTime && endTime) {
              const start = new Date(`${activeDateKey}T${startTime}:00`)
              const end = new Date(`${activeDateKey}T${endTime}:00`)
              if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
                rawBlocks.push({
                  id: 'selection',
                  start,
                  end,
                  type: 'selection',
                })
              }
            }

            // 3. Free slots
            if (rawFree > 0) {
              freeIntervals.forEach((free, idx) => {
                const fStartH = Math.floor(free.start / 60)
                const fStartM = free.start % 60
                const fEndH = Math.floor(free.end / 60)
                const fEndM = free.end % 60
                const start = new Date(`${activeDateKey}T${toHHMM(fStartH, fStartM)}:00`)
                const end = new Date(`${activeDateKey}T${toHHMM(fEndH, fEndM)}:00`)
                if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
                  rawBlocks.push({
                    id: `free-${idx}`,
                    start,
                    end,
                    type: 'free',
                    data: free,
                  })
                }
              })
            }


            const { lanes, blocks } = assignLanesLocal(rawBlocks)
            const trackHeight = lanes * 52 + 16

            return (
              <div ref={timelineRef} className="space-y-3 pt-4 border-t border-outline-variant/10 scroll-mt-4">
                <div className="flex items-center justify-between">
                  <p className="font-label text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
                    Daily Timeline — {activeDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                  <span className="text-xs text-on-surface-variant">Select free slot or use timeline below</span>
                </div>
                <div className="overflow-hidden rounded-[22px] border border-outline-variant/20 bg-surface-container-lowest">
                  {/* Header */}
                  <div className="px-2 py-3 text-center text-xs font-bold text-on-surface border-b border-surface-container">
                    Schedule for {activeDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>

                  {/* Horizontal Scrollable Container */}
                  <div ref={timelineScrollContainerRef} className="overflow-x-auto relative app-hide-scrollbar">
                    <div className="flex flex-col relative" style={{ width: `${totalHours * cellWidth}px` }}>
                      {/* X-Axis Hours Header */}
                      <div className="flex border-b border-surface-container/60 bg-surface-container-low/20">
                        {Array.from({ length: totalHours }).map((_, i) => {
                          const hour = timelineStartHour + i
                          const period = hour >= 12 ? 'PM' : 'AM'
                          const displayHour = hour % 12 || 12
                          return (
                            <div
                              key={hour}
                              className="text-[10px] font-bold text-on-surface-variant text-center border-r border-surface-container/60 last:border-r-0 py-2 select-none"
                              style={{ width: `${cellWidth}px` }}
                            >
                              {displayHour} {period}
                            </div>
                          )
                        })}
                      </div>

                      {/* Track Area */}
                      <div
                        className="relative bg-surface-container-low/5"
                        style={{
                          width: `${totalHours * cellWidth}px`,
                          height: `${trackHeight}px`
                        }}
                      >
                        {/* Grid lines */}
                        {Array.from({ length: totalHours }).map((_, i) => (
                          <div
                            key={`grid-line-${i}`}
                            className="absolute top-0 bottom-0 border-r border-surface-container/30 pointer-events-none"
                            style={{ left: `${i * cellWidth}px` }}
                          />
                        ))}

                        {/* Render all stacked blocks */}
                        {blocks.map((block) => {
                          const hStyle = getHorizontalStyle(block.start, block.end, timelineStartHour, cellWidth)
                          if (!hStyle) return null

                          const style = {
                            ...hStyle,
                            top: `${8 + block.lane * 52}px`,
                            height: '40px',
                          }

                          if (block.type === 'event') {
                            const event = block.data
                            const palette = resolveEventPalette(event)
                            return (
                              <div
                                key={block.id}
                                className={clsx(
                                  "absolute overflow-hidden rounded-xl border-l-4 px-3 py-1 flex flex-col justify-center pointer-events-none select-none",
                                  palette.block
                                )}
                                style={style}
                              >
                                <p className="truncate font-bold text-[11px] leading-tight">{event.title || '(No title)'}</p>
                                <p className="truncate text-[9px] opacity-85 mt-0.5 leading-none">
                                  {formatClock(event.startTime)} - {formatClock(event.endTime)}
                                </p>
                              </div>
                            )
                          }

                          if (block.type === 'selection') {
                            return (
                              <div
                                key={block.id}
                                className="absolute rounded-xl border border-dashed border-primary bg-primary/10 pointer-events-none z-10"
                                style={style}
                              />
                            )
                          }

                          if (block.type === 'free') {
                            const free = block.data
                            const durationMin = free.end - free.start
                            const durationHrs = Math.round((durationMin / 60) * 10) / 10
                            const availableHrs = Math.min(durationHrs, rawFree)

                            return (
                              <button
                                key={block.id}
                                type="button"
                                onClick={() => handleFreeSlotClick(free)}
                                className="absolute rounded-xl flex items-center justify-between px-3 cursor-pointer group border-2 border-dashed border-primary text-primary bg-primary/5 hover:bg-primary/10 transition-all z-20 select-none text-left"
                                style={style}
                              >
                                <div className="flex flex-col min-w-0">
                                  <span className="text-[10px] font-extrabold leading-tight truncate text-primary uppercase tracking-wider">
                                    Available Slot
                                  </span>
                                  <span className="text-[9px] font-medium mt-0.5 text-primary/70 leading-none">
                                    {availableHrs}h capacity left
                                  </span>
                                </div>
                                <span className="material-symbols-outlined text-sm text-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                  add_task
                                </span>
                              </button>
                            )
                          }


                          return null
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })()}

          {/* Selected range + estimated hours */}
          {mode === 'select-job' ? (
            <div ref={bottomFieldsRef} className="space-y-4">
              {/* Date Card */}
              <div className="flex items-center justify-between rounded-md bg-surface-container-low/40 p-4 border border-outline-variant/10">
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-primary">calendar_today</span>
                  <div>
                    <p className="text-[10px] font-bold uppercase text-on-surface-variant">
                      Start Date <span className="text-error">*</span>
                    </p>
                    <AppDateInput
                      value={rangeStart ? toDateKey(rangeStart) : ''}
                      onChange={(value) => {
                        const d = value ? new Date(value) : null
                        setRangeStart(d)
                        if (mode === 'select-job') {
                          setRangeEnd(d)
                        }
                        if (d) setActiveDate(d)
                      }}
                      inputClassName="border-none bg-transparent p-0 pr-8 text-sm font-bold shadow-none"
                      buttonClassName="hover:bg-surface-container-low"
                    />
                  </div>
                </div>
                <span className="material-symbols-outlined text-outline-variant">arrow_forward</span>
                <div className="text-right">
                  <p className="text-[10px] font-bold uppercase text-on-surface-variant">
                    End Date <span className="text-error">*</span>
                  </p>
                  <AppDateInput
                    value={rangeEnd ? toDateKey(rangeEnd) : ''}
                    onChange={(value) => {
                      const d = value ? new Date(value) : null
                      setRangeEnd(d)
                      if (mode === 'select-job') {
                        setRangeStart(d)
                      }
                      if (d) setActiveDate(d)
                    }}
                    inputClassName="border-none bg-transparent p-0 pr-8 text-right text-sm font-bold shadow-none"
                    buttonClassName="hover:bg-surface-container-low"
                  />
                </div>
              </div>

              {/* Time Card */}
              <div className="flex items-center justify-between rounded-md bg-surface-container-low/40 p-4 border border-outline-variant/10">
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-primary">schedule</span>
                  <div>
                    <p className="text-[10px] font-bold uppercase text-on-surface-variant mb-1">
                      Start Time
                    </p>
                    <AppTimeInput
                      value={startTime || ''}
                      onChange={(val) => setStartTime(val)}
                      inputClassName="border-none bg-transparent p-0 pr-8 text-sm font-bold shadow-none"
                      buttonClassName="hover:bg-surface-container-low"
                    />
                  </div>
                </div>
                <span className="material-symbols-outlined text-outline-variant">arrow_forward</span>
                <div className="text-right">
                  <p className="text-[10px] font-bold uppercase text-on-surface-variant mb-1">
                    End Time
                  </p>
                  <AppTimeInput
                    value={endTime || ''}
                    onChange={(val) => setEndTime(val)}
                    inputClassName="border-none bg-transparent p-0 pr-8 text-right text-sm font-bold shadow-none"
                    buttonClassName="hover:bg-surface-container-low"
                  />
                </div>
              </div>

              {/* Estimated Time Section */}
              <div className="rounded-md bg-surface-container-low/40 p-4 border border-outline-variant/10 space-y-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">
                  Estimated Time <span className="text-error">*</span>
                </span>
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-1">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Hours</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={plannedHours}
                      onChange={(event) => setPlannedHours(event.target.value)}
                      className="w-full rounded-md border border-outline-variant/30 bg-surface-container-lowest px-4 py-3 text-sm text-on-surface outline-none focus:border-primary/45"
                      placeholder="Hours"
                    />
                  </div>
                  <div className="grid gap-1">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Minutes</span>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      step="1"
                      value={plannedMinutes}
                      onChange={(event) => setPlannedMinutes(event.target.value)}
                      className="w-full rounded-md border border-outline-variant/30 bg-surface-container-lowest px-4 py-3 text-sm text-on-surface outline-none focus:border-primary/45"
                      placeholder="Minutes"
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-6">
              <div className="flex flex-col space-y-2">
                <label className="text-[10px] font-bold text-outline uppercase tracking-widest">Start Date</label>
                <input
                  className="w-full bg-surface-container-low border border-outline-variant/30 rounded-full px-4 py-3 text-sm font-semibold text-on-surface outline-none"
                  readOnly
                  type="text"
                  value={selStart ? toDateKey(selStart) : '—'}
                />
              </div>
              <div className="flex flex-col space-y-2">
                <label className="text-[10px] font-bold text-outline uppercase tracking-widest">End Date</label>
                <input
                  className="w-full bg-surface-container-low border border-outline-variant/30 rounded-full px-4 py-3 text-sm font-semibold text-on-surface outline-none"
                  readOnly
                  type="text"
                  value={selEnd ? toDateKey(selEnd) : '—'}
                />
              </div>
              <div className="flex flex-col space-y-2">
                <label className="text-[10px] font-bold text-outline uppercase tracking-widest">Estimated Hours</label>
                <input
                  className="w-full bg-surface-container-low border border-outline-variant/30 rounded-full px-4 py-3 text-sm font-semibold text-on-surface outline-none"
                  type="text"
                  value={estimatedHours}
                  onChange={(e) => setEstimatedHours(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Warnings / Overallocation Confirm prompt (Only in assign mode) */}
          {mode === 'assign' && assignWarnings && assignWarnings.length > 0 && (
            <div className="rounded-xl bg-error-container px-4 py-3">
              {assignWarnings.map((w, i) => (
                <p key={i} className="flex items-start gap-2 text-xs font-semibold text-error">
                  <span className="material-symbols-outlined text-sm shrink-0">warning</span>
                  {w}
                </p>
              ))}
              {requiresConfirm && (
                <p className="text-[10px] text-error/80 mt-2">
                  Click <strong>Confirm &amp; Proceed</strong> below to assign anyway.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-6 bg-surface-container-low/40 flex items-center justify-end gap-4 border-t border-outline-variant/10 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-8 py-3 rounded-full text-sm font-bold text-outline hover:bg-surface-container-low transition-colors bg-transparent border-0"
          >
            Cancel
          </button>
          {mode === 'select' || mode === 'select-job' ? (
            <button
              type="button"
              onClick={handleAssign}
              disabled={!isSelectedRangeSet}
              className="px-10 py-3 bg-primary text-on-primary rounded-full text-sm font-bold shadow-lg shadow-primary/20 hover:bg-primary/95 transition-all transform active:scale-95 disabled:opacity-50"
            >
              Select Dates &amp; Hours
            </button>
          ) : preSelectedTask ? (
            <button
              type="button"
              onClick={() => {
                if (assignWarnings?.length > 0 && !requiresConfirm) {
                  setRequiresConfirm(true)
                  return
                }
                handleAssign()
              }}
              disabled={!isSelectedRangeSet || isAssigning}
              className="px-10 py-3 bg-primary text-on-primary rounded-full text-sm font-bold shadow-lg shadow-primary/20 hover:bg-primary/95 transition-all transform active:scale-95 disabled:opacity-50"
            >
              {isAssigning
                ? (isUpdate ? 'Saving...' : 'Assigning...')
                : requiresConfirm
                  ? 'Confirm & Proceed'
                  : isUpdate ? 'Save Changes' : 'Assign Resource'}
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="px-10 py-3 bg-primary text-on-primary rounded-full text-sm font-bold shadow-lg shadow-primary/20 hover:bg-primary/95 transition-all transform active:scale-95"
            >
              Close
            </button>
          )}
        </div>

      </div>
    </div>
  )
}
